import React, { useEffect, useMemo, useState } from "react";
import { translateUi } from './i18n/index';
import { useUiLocale } from "./useUiLocale";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { STORAGE_QUOTA_DASHBOARD_BANNER_MESSAGE } from "./storageQuotaEvidence";
import {
  Activity,
  AlertTriangle,
  Award,
  Briefcase,
  Clock,
  FileCheck,
  ClipboardList,
  Shield,
  Stethoscope,
  TrainFront,
  Upload,
  Users,
  Wrench,
} from "./icons";
import { DashboardForYouPanel, type DashboardInboxItem } from "./DashboardForYouPanel";
import {
  DASHBOARD_GRADIENT_PANE_SHELL,
  DashboardGradientPaneGlows,
} from "./dashboardGradientPane";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Shared with My progress / team compliance panes. */
const GRADIENT_PANE_SHELL = DASHBOARD_GRADIENT_PANE_SHELL;

function GradientPaneGlows() {
  return <DashboardGradientPaneGlows />;
}

/** Map image under a solid wash (no blur). Opacity tuned per theme so text stays readable. */
const GBR_TILE_BG: React.CSSProperties = {
  backgroundImage: "url('/GBR_BG.png')",
  backgroundSize: "cover",
  backgroundPosition: "center",
};

function GbrMapWash({ isDarkMode }: { isDarkMode: boolean }) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute inset-0",
        isDarkMode ? "bg-slate-950/[0.94]" : "bg-white/90"
      )}
      aria-hidden
    />
  );
}

const KPI_ORDER = [
  "active",
  "offtrack",
  "overdueComp",
  "dueSoonComp",
  "expiredMed",
  "dueSoonMed",
  "continuousDueSoon",
  "continuousBlocked",
] as const;
type KpiId = (typeof KPI_ORDER)[number];

function canViewCompanyWideTasks(title?: string): boolean {
  const t = (title || "").toLowerCase();
  return t.includes("administrator") || t.includes("admin") || t.includes("manager");
}

function CompactStatCard({
  title,
  value,
  icon: Icon,
  color,
  onClick,
  isDarkMode,
  variant = "default",
}: {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  onClick?: () => void;
  isDarkMode: boolean;
  variant?: "default" | "gradient";
}) {
  const isGradient = variant === "gradient";
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      onClick={onClick}
      style={isGradient ? undefined : GBR_TILE_BG}
      className={cn(
        "relative overflow-hidden rounded-xl p-4 transition-colors",
        "cursor-pointer",
        isGradient
          ? "border border-white/15 bg-white/[0.06] backdrop-blur-sm hover:bg-white/[0.1]"
          : cn(
              "rounded-2xl shadow-md transition-shadow",
              isDarkMode
                ? "border border-slate-500 ring-1 ring-slate-400/30 hover:ring-slate-400/50 hover:shadow-lg hover:shadow-black/40"
                : "border border-slate-200 ring-1 ring-slate-900/[0.04] hover:shadow-md"
            )
      )}
    >
      {!isGradient && <GbrMapWash isDarkMode={isDarkMode} />}
      <div className="relative z-10 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className={cn("p-2.5 rounded-xl shrink-0 shadow-sm ring-1 ring-black/10", color)}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <p
            className={cn(
              "text-xs font-semibold uppercase tracking-wide leading-tight",
              isGradient ? "text-slate-300" : isDarkMode ? "text-slate-100" : "text-slate-700"
            )}
          >
            {title}
          </p>
        </div>
        <p
          className={cn(
            "text-xl font-bold tabular-nums shrink-0",
            isGradient ? "text-white" : isDarkMode ? "text-white" : "text-slate-900"
          )}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function SectionChrome({
  title,
  subtitle,
  children,
  isDarkMode,
  variant = "default",
  icon: Icon,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  isDarkMode: boolean;
  variant?: "default" | "gradient";
  icon?: React.ComponentType<{ className?: string }>;
}) {
  if (variant === "gradient") {
    return (
      <section className={GRADIENT_PANE_SHELL}>
        <GradientPaneGlows />
        <div className="relative z-10">
          <div className="mb-4 flex flex-wrap items-start gap-3">
            <div className="flex min-w-0 items-center gap-2">
              {Icon ? (
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-indigo-100 ring-1 ring-white/20">
                  <Icon className="h-5 w-5" />
                </span>
              ) : null}
              <div className="min-w-0">
                <h2 className="text-base font-bold tracking-tight text-white">{title}</h2>
                {subtitle ? <p className="mt-0.5 text-xs text-slate-300">{subtitle}</p> : null}
              </div>
            </div>
          </div>
          {children}
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn(
        "rounded-2xl border p-5 sm:p-6 shadow-sm",
        isDarkMode
          ? "border-slate-500 bg-slate-900/90 ring-1 ring-slate-500/40 shadow-black/30"
          : "border-slate-200 bg-white/90 ring-1 ring-slate-900/[0.03]"
      )}
    >
      <div className="mb-4">
        <h2 className={cn("text-sm font-bold tracking-tight", isDarkMode ? "text-slate-50" : "text-slate-900")}>
          {title}
        </h2>
        {subtitle && (
          <p className={cn("text-xs mt-0.5", isDarkMode ? "text-slate-300" : "text-slate-500")}>{subtitle}</p>
        )}
      </div>
      {children}
    </section>
  );
}

export type CompanyStatsLite = {
  totalActive?: number;
  offTrackCount?: number;
  overdueComp?: number;
  dueSoonComp?: number;
  expiredMed?: number;
  dueSoonMed?: number;
  continuousDueSoon?: number;
  continuousBlocked?: number;
};

export function CompanyDashboardBody({
  isMobileOrTablet,
  isCompanyAdmin,
  companyAddons,
  isCompanyAdminUser,
  hasCompanyDoc,
  stats,
  isDarkMode,
  onTab,
  setEmployeeFilter,
  openDashboardDetails,
  companyCode,
  currentUserEmail,
  currentUserTitle,
  addonsRefreshTrigger,
  inboxItems = [],
  onInboxItemClick,
  setupIssues,
  onOpenSetup,
  onOpenEmployee,
  canRunQAVerifications = false,
  currentUserName,
  onNavigateToQaFix,
}: {
  isMobileOrTablet?: boolean;
  isCompanyAdmin: boolean;
  companyAddons: string[];
  isCompanyAdminUser: boolean;
  hasCompanyDoc: boolean;
  stats: CompanyStatsLite | null;
  isDarkMode: boolean;
  onTab: (tab: string) => void;
  setEmployeeFilter: (f: "all" | "active" | "off-track") => void;
  openDashboardDetails: (title: string, type: string) => void;
  companyCode?: string;
  currentUserEmail?: string;
  currentUserTitle?: string;
  /** Increment when company add-ons list may have changed (e.g. after purchase). */
  addonsRefreshTrigger?: number;
  inboxItems?: DashboardInboxItem[];
  onInboxItemClick?: (item: DashboardInboxItem) => void;
  setupIssues?: string[] | null;
  onOpenSetup?: () => void;
  onOpenEmployee?: (employeeId: string) => void;
  canRunQAVerifications?: boolean;
  currentUserName?: string;
  onNavigateToQaFix?: (target: import("./QAVerificationReport").VerificationFixTarget & { employeeId: string }) => void;
}) {
  const uiLocale = useUiLocale();
  const taskAddonActive = companyAddons.includes("task-assignment");
  const canTaskMetrics = canViewCompanyWideTasks(currentUserTitle);
  const [openTasksCount, setOpenTasksCount] = useState<number | null>(null);
  const [storageSuspended, setStorageSuspended] = useState<boolean | null>(null);

  useEffect(() => {
    if (!companyCode?.trim() || !currentUserEmail?.trim()) {
      setStorageSuspended(null);
      return;
    }
    let cancelled = false;
    setStorageSuspended(null);
    fetch(`/api/storage/company/${encodeURIComponent(companyCode.trim())}`, {
      headers: {
        "X-User-Email": currentUserEmail.trim(),
        "X-Company-Code": companyCode.trim(),
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { status?: { suspended?: boolean } } | null) => {
        if (cancelled) return;
        setStorageSuspended(data?.status?.suspended === true);
      })
      .catch(() => {
        if (!cancelled) setStorageSuspended(null);
      });
    return () => {
      cancelled = true;
    };
  }, [companyCode, currentUserEmail, addonsRefreshTrigger]);

  useEffect(() => {
    if (!taskAddonActive || !canTaskMetrics || !companyCode?.trim() || !currentUserEmail?.trim()) {
      setOpenTasksCount(null);
      return;
    }
    let cancelled = false;
    setOpenTasksCount(null);
    fetch(`/api/tasks?companyCode=${encodeURIComponent(companyCode.trim())}`, {
      headers: {
        "X-User-Email": currentUserEmail.trim(),
        "X-Company-Code": companyCode.trim(),
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { tasks?: { status?: string }[] } | null) => {
        if (cancelled || !d?.tasks || !Array.isArray(d.tasks)) {
          if (!cancelled) setOpenTasksCount(0);
          return;
        }
        const open = d.tasks.filter((row) => (row.status || "open").trim().toLowerCase() !== "done").length;
        if (!cancelled) setOpenTasksCount(open);
      })
      .catch(() => {
        if (!cancelled) setOpenTasksCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [taskAddonActive, canTaskMetrics, companyCode, currentUserEmail, currentUserTitle, addonsRefreshTrigger]);

  const kpiNodes = useMemo(() => {
    const map: Record<
      KpiId,
      {
        title: string;
        value: number;
        icon: React.ComponentType<{ className?: string }>;
        color: string;
        onClick?: () => void;
      }
    > = {
      active: {
        title: translateUi("Active & On Track"),
        value: stats?.totalActive ?? 0,
        icon: Users,
        color: "bg-slate-800",
        onClick: () => {
          setEmployeeFilter("active");
          onTab("employees");
        },
      },
      offtrack: {
        title: translateUi("Off Track"),
        value: stats?.offTrackCount ?? 0,
        icon: Activity,
        color: "bg-rose-600",
        onClick: () => {
          setEmployeeFilter("off-track");
          onTab("employees");
        },
      },
      overdueComp: {
        title: translateUi("Overdue Assessments"),
        value: stats?.overdueComp ?? 0,
        icon: AlertTriangle,
        color: "bg-rose-600",
        onClick: () => {
          openDashboardDetails(translateUi("Overdue Assessments"), "overdueComp");
          onTab("dashboard-details");
        },
      },
      dueSoonComp: {
        title: translateUi("Assessments Due in 30 Days"),
        value: stats?.dueSoonComp ?? 0,
        icon: Clock,
        color: "bg-amber-500",
        onClick: () => {
          openDashboardDetails(translateUi("Assessments Due in 30 Days"), "dueSoonComp");
          onTab("dashboard-details");
        },
      },
      expiredMed: {
        title: translateUi("Medical Expired"),
        value: stats?.expiredMed ?? 0,
        icon: AlertTriangle,
        color: "bg-rose-600",
        onClick: () => {
          openDashboardDetails(translateUi("Medical Expired"), "expiredMed");
          onTab("dashboard-details");
        },
      },
      dueSoonMed: {
        title: translateUi("Upcoming Medicals Due in 30 Days"),
        value: stats?.dueSoonMed ?? 0,
        icon: Clock,
        color: "bg-amber-500",
        onClick: () => {
          openDashboardDetails(translateUi("Upcoming Medicals Due in 30 Days"), "dueSoonMed");
          onTab("dashboard-details");
        },
      },
      continuousDueSoon: {
        title: translateUi("Continuous Renewals Due"),
        value: stats?.continuousDueSoon ?? 0,
        icon: Clock,
        color: "bg-amber-500",
        onClick: () => {
          openDashboardDetails(translateUi("Continuous Renewals Due (14 Days)"), "continuousDueSoon");
          onTab("dashboard-details");
        },
      },
      continuousBlocked: {
        title: translateUi("Continuous Blocked"),
        value: stats?.continuousBlocked ?? 0,
        icon: AlertTriangle,
        color: "bg-rose-600",
        onClick: () => {
          openDashboardDetails(translateUi("Continuous Cycles Blocked"), "continuousBlocked");
          onTab("dashboard-details");
        },
      },
    };
    return map;
  }, [stats, setEmployeeFilter, onTab, openDashboardDetails, uiLocale]);

  const quickLinks = useMemo(() => {
    const qa = companyAddons.includes("qa-verifications");
    const tasks = companyAddons.includes("task-assignment");
    const safetyBriefs = companyAddons.includes("safety-briefs");
    const rows: { tab: string; label: string; icon: React.ComponentType<{ className?: string }>; show: boolean }[] = [
      { tab: "team", label: translateUi("Team Management"), icon: Shield, show: true },
      { tab: "tasks", label: translateUi("Tasks"), icon: ClipboardList, show: tasks },
      { tab: "safety-briefs", label: translateUi("Safety Briefs"), icon: Shield, show: safetyBriefs },
      { tab: "medicals", label: translateUi("Medicals"), icon: Stethoscope, show: true },
      { tab: "custom-job-roles", label: translateUi("Custom job roles"), icon: Briefcase, show: isCompanyAdminUser },
      { tab: "grading-scale", label: translateUi("Grading scale"), icon: Award, show: isCompanyAdminUser },
      { tab: "company-standards", label: translateUi("Company standards"), icon: ClipboardList, show: isCompanyAdminUser },
      { tab: "traction-routes", label: translateUi("Traction & routes"), icon: TrainFront, show: isCompanyAdminUser },
      { tab: "cycle-builder", label: translateUi("Cycle Builder"), icon: Wrench, show: isCompanyAdmin },
      { tab: "role-permissions", label: translateUi("Role permissions"), icon: Shield, show: isCompanyAdminUser && hasCompanyDoc },
      { tab: "company-logo", label: translateUi("Company logo"), icon: Upload, show: isCompanyAdminUser && hasCompanyDoc },
      { tab: "qa-verifications", label: translateUi("QA Verifications"), icon: FileCheck, show: qa },
    ];
    return rows.filter((r) => r.show);
  }, [companyAddons, isCompanyAdmin, isCompanyAdminUser, hasCompanyDoc, uiLocale]);

  return (
    <div className="flex w-full flex-1 flex-col gap-5 sm:gap-6 min-h-0 lg:min-h-full">
      {storageSuspended === true && (
        <div
          role="alert"
          className={cn(
            "rounded-2xl border p-4 sm:p-5 flex gap-3 sm:gap-4 shadow-sm",
            isDarkMode
              ? "border-amber-500/50 bg-amber-950/50 ring-1 ring-amber-500/30"
              : "border-amber-300 bg-amber-50 ring-1 ring-amber-200/80"
          )}
        >
          <div
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
              isDarkMode ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700"
            )}
            aria-hidden
          >
            <AlertTriangle className="h-6 w-6" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <p className={cn("text-sm sm:text-base font-semibold", isDarkMode ? "text-amber-100" : "text-amber-950")}>
              {translateUi("Storage limit reached")}
            </p>
            <p
              className={cn(
                "text-sm mt-2 leading-relaxed",
                isDarkMode ? "text-amber-100/90" : "text-amber-950/90"
              )}
            >
              {translateUi(STORAGE_QUOTA_DASHBOARD_BANNER_MESSAGE)}
            </p>
          </div>
        </div>
      )}
      {!isCompanyAdmin && (
        <SectionChrome
          isDarkMode={isDarkMode}
          title={translateUi("Shortcuts")}
          subtitle={translateUi("Same destinations as Administration and related items in the sidebar")}
        >
          <div
            className={cn(
              "grid gap-3",
              isMobileOrTablet ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 sm:grid-cols-3 xl:grid-cols-4"
            )}
          >
            {quickLinks.map(({ tab, label, icon: Icon }) => (
              <button
                key={tab}
                type="button"
                onClick={() => onTab(tab)}
                style={GBR_TILE_BG}
                className={cn(
                  "relative overflow-hidden rounded-2xl p-4 text-left min-h-[52px] w-full shadow-md transition-shadow",
                  isDarkMode
                    ? "border border-slate-500 ring-1 ring-slate-400/30 hover:ring-slate-400/50 hover:shadow-lg hover:shadow-black/40"
                    : "border border-slate-200 ring-1 ring-slate-900/[0.04] hover:shadow-md"
                )}
              >
                <GbrMapWash isDarkMode={isDarkMode} />
                <span className="relative z-10 flex items-center gap-3 w-full min-w-0">
                  <span className="p-2 rounded-xl bg-indigo-600 text-white shrink-0 shadow-sm ring-1 ring-white/20">
                    <Icon className="w-4 h-4" />
                  </span>
                  <span
                    className={cn(
                      "text-sm font-semibold truncate",
                      isDarkMode ? "text-slate-50" : "text-slate-800"
                    )}
                  >
                    {label}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </SectionChrome>
      )}

      <div className="shrink-0">
      <SectionChrome
        isDarkMode={isDarkMode}
        variant="gradient"
        icon={Shield}
        title={translateUi("Compliance overview")}
        subtitle={translateUi("Live counts from your company data")}
      >
        <div
          className={cn(
            "grid gap-3 sm:gap-4",
            isMobileOrTablet ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 lg:grid-cols-3"
          )}
        >
          {KPI_ORDER.map((id) => {
            const node = kpiNodes[id];
            return (
              <CompactStatCard
                key={id}
                isDarkMode={isDarkMode}
                variant="gradient"
                title={node.title}
                value={node.value}
                icon={node.icon}
                color={node.color}
                onClick={node.onClick}
              />
            );
          })}
          {taskAddonActive && canTaskMetrics ? (
            <CompactStatCard
              key="open-tasks"
              isDarkMode={isDarkMode}
              variant="gradient"
              title={translateUi("Open Tasks")}
              value={openTasksCount === null ? "—" : openTasksCount}
              icon={ClipboardList}
              color="bg-violet-600"
              onClick={() => onTab("tasks")}
            />
          ) : null}
        </div>
      </SectionChrome>
      </div>

      <div className="flex min-h-[22rem] flex-1 flex-col sm:min-h-[26rem]">
        <DashboardForYouPanel
          isDarkMode={isDarkMode}
          isMobileOrTablet={isMobileOrTablet}
          companyCode={companyCode}
          currentUserEmail={currentUserEmail}
          companyAddons={companyAddons}
          onTab={onTab}
          inboxItems={inboxItems}
          onInboxItemClick={onInboxItemClick || (() => {})}
          setupIssues={setupIssues}
          onOpenSetup={onOpenSetup}
          refreshTrigger={addonsRefreshTrigger}
          onOpenEmployee={onOpenEmployee}
          canRunQAVerifications={canRunQAVerifications}
          currentUserName={currentUserName}
          onNavigateToQaFix={onNavigateToQaFix}
        />
      </div>

    </div>
  );
}
